import React, { useRef, useState } from "react";
import {
  Autocomplete,
  Button,
  CardHeader,
  Chip,
  CircularProgress,
  DataGrid,
  Dialog,
  DialogContent,
  Icon,
  IconButton,
  Loading,
  TextField,
  Tooltip,
} from "../../../../../components";
import { Grid } from "@mui/material";
import { filtrosUrl, value } from "../../../../../helpers";
import { formatDate, formatPrice, responseErros } from "../../../../../utils";
import {
  ContasReceberService,
  notification,
  PessoaService,
} from "../../../../../services";
import { format } from "date-fns";

const CompensacaoDialog = ({
  open,
  setOpen,
  filtrosDialog,
  setFiltrosDialog,
  buscarContasReceber,
}) => {
  const inputCliente = useRef();
  const [loading, setLoading] = useState(false);
  const [loadingAutoComplete, setLoadingAutoComplete] = useState(false);
  const [listaPessoas, setListaPessoas] = useState([]);
  const [listaNotasCredito, setListaNotasCredito] = useState([]);
  const [listaNotasBaixar, setListaNotasBaixar] = useState([]);

  const [notasCreditoSelectionModel, setNotasCreditoSelectionModel] = useState(
    []
  );
  const [notasBaixarSelectionModel, setNotasBaixarSelectionModel] = useState(
    []
  );

  const pessoaService = new PessoaService();
  const contasReceberService = new ContasReceberService();

  const colunas = [
    {
      field: "status",
      headerName: "Status",
      flex: 100,
      renderCell: (params) => {
        switch (params.value) {
          case "BAIXADO_PARCIAL":
            return (
              <Tooltip title="Baixado Parcial" arrow placement="right">
                <Icon>
                  <i
                    className="ph-fill ph-circle"
                    style={{ color: "#EA9D2B", fontSize: 18 }}
                  ></i>
                </Icon>
              </Tooltip>
            );
          default:
            return (
              <Tooltip title="Aberto" arrow placement="right">
                <Icon>
                  <i
                    className="ph-fill ph-circle"
                    style={{ color: "#4FBB53", fontSize: 18 }}
                  ></i>
                </Icon>
              </Tooltip>
            );
        }
      },
    },
    {
      field: "numeroTitulo",
      headerName: "Nº Título",
      flex: 200,
      filtrar: true,
    },
    {
      field: "pagadorId",
      headerName: "Cliente",
      flex: 400,
      sortable: false,
      filtroPessoa: true,
      valueGetter: (params) =>
        listaPessoas.find((item) => item.id == params.value)?.nomeRazaoSocial,
    },
    {
      field: "saldoReceber",
      headerName: "Saldo",
      flex: 300,
      sortable: false,
      valueGetter: (params) => formatPrice(params.value),
    },
    {
      field: "dataVencimento",
      headerName: "Data de Vencimento",
      flex: 250,
      renderCell: (params) => {
        const value = format(formatDate.received(params.value), "dd/MM/yyyy");
        if (new Date(params.value) < new Date()) {
          return (
            <div className="d-flex h-50 gap-2 align-items-center">
              <span
                style={{
                  height: "100%",
                  width: 4,
                  borderRadius: "30%",
                  background: "red",
                }}
              ></span>
              {value}
            </div>
          );
        }
        return <div>{value}</div>;
      },
    },
  ];

  function onClose() {
    setOpen(false);
    setListaPessoas([]);
    setListaNotasCredito([]);
    setListaNotasBaixar([]);
    setFiltrosDialog({});
  }

  const onSearchChangePessoas = (event, inputRef) => {
    if (event.key === "Enter" && inputRef.current === document.activeElement) {
      const eventValue = event.target?.value;
      let filtros = {
        nomeRazaoSocial: eventValue,
        apelidoFantasia: eventValue,
      };
      setLoadingAutoComplete(true);
      buscarPessoas(filtros);
    }
  };

  const buscarPessoas = async (filtros, listaIds) => {
    let filtro = { ativado: true, attributes: ["id", "nomeRazaoSocial"] };
    if (listaIds?.length) {
      filtro = {
        ...filtro,
        id: listaIds,
        nonPaginated: true,
      };
    } else {
      filtro = { ...filtro, ...filtros, limite: 15 };
    }
    const result = await pessoaService.getAllFiltroAvancadoSimplificada(filtro);
    if (!result.isAxiosError) {
      const listaPessoas = result.data;
      if (listaIds?.length) {
        setListaPessoas(listaPessoas);
      } else setListaPessoas(listaPessoas?.rows);
      setLoadingAutoComplete(false);
    } else {
      responseErros(result);
    }
  };

  const onChangeAutocomplete = (name, value) => {
    setFiltrosDialog({
      ...filtrosDialog,
      [name]: value ? value.id : undefined,
    });
    if (name === "pagadorId" && value) {
      buscarContasReceberCompensar({
        pagadorId: value.id,
        status: ["ABERTO", "BAIXADO_PARCIAL"],
        restritiva: true,
      });
    } else {
      setListaNotasCredito([]);
      setListaNotasBaixar([]);
    }
  };

  const buscarContasReceberCompensar = async (filtro = {}) => {
    setLoading(true);
    const result = await contasReceberService.getAllContasCompensar(filtro);
    if (!result.isAxiosError) {
      if (result.data) {
        setListaNotasCredito(result.data.credito);
        setListaNotasBaixar(result.data.abertas);
      }
    } else {
      responseErros(result);
    }
    setLoading(false);
  };

  function ChipCompensacao({ label, color }) {
    return (
      <Chip
        label={label}
        className="mb-2"
        color={color}
        sx={{
          borderRadius: "4px",
          height: "23px",
          width: "100%",
          clipPath: `polygon(0% 0%, calc(100% - 10px) 0%, 100% 50%, calc(100% - 10px) 100%, 10px 100%, 0% 100%)`,
          "& .MuiChip-label": {
            fontWeight: "600",
            fontSize: "11px",
            letterSpacing: ".2px",
          },
        }}
      />
    );
  }

  function onSubmit() {
    const response = contasReceberService.compensar({
      ids: [...notasCreditoSelectionModel, ...notasBaixarSelectionModel],
    });
    setLoading(false);
    if (!response.isAxiosError) {
      notification.cadastroSucesso();
      onClose();
      const filtros = filtrosUrl.get();
      buscarContasReceber(filtros);
    } else {
      responseErros(response);
    }
  }

  return (
    <Dialog fullWidth maxWidth="xl" open={open} onClose={() => onClose()}>
      <CardHeader
        title="Compensação"
        action={
          <IconButton
            color="inherit"
            onClick={() => onClose()}
            sx={{
              backgroundColor: "#EDEEF0",
              "&:hover": { backgroundColor: "#B6BABC" },
            }}
          >
            <i
              className="ph-bold ph-x"
              style={{ fontSize: "clamp(17px, 4vw, 20px)" }}
            ></i>
          </IconButton>
        }
      />
      <DialogContent>
        <Grid container spacing={2} direction={"row"} className="mb-4">
          <Grid item xs={12} lg={6}>
            <Autocomplete
              id="pagadorId"
              name="pagadorId"
              options={listaPessoas}
              autoHighlight
              getOptionLabel={(option) =>
                option.nomeRazaoSocial
                  ? `${option.id}` + ` - ` + `${option.nomeRazaoSocial}`
                  : ""
              }
              getOptionSelected={(option, value) =>
                option?.nomeRazaoSocial === value?.nomeRazaoSocial
              }
              noOptionsText="Sem opções"
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Cliente"
                  variant={"outlined"}
                  inputRef={inputCliente}
                  onKeyDown={(e) => onSearchChangePessoas(e, inputCliente)}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <React.Fragment>
                        {loadingAutoComplete ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </React.Fragment>
                    ),
                  }}
                />
              )}
              onChange={(e, value) => onChangeAutocomplete("pagadorId", value)}
              value={value.autoComplete(listaPessoas, filtrosDialog?.pagadorId)}
            />
          </Grid>
        </Grid>
        <Grid container spacing={2} direction={"row"}>
          <Grid item xs={12} lg={6}>
            <ChipCompensacao label={"Notas de Crédito"} color={"success"} />
            <DataGrid
              rows={listaNotasCredito}
              columns={colunas}
              checkboxSelection
              selectionModel={notasCreditoSelectionModel}
              onRowSelectionModelChange={(selectedIds) => {
                setNotasCreditoSelectionModel(selectedIds);
              }}
              paginationMode={"server"}
              disableAcoes
              noFilter
            />
            <Grid
              container
              xs={12}
              justifyContent={"flex-end"}
              className="mt-2"
            >
              <h7>
                Total Notas de Crédito:{" "}
                <span className="fw-bolder">
                  {formatPrice(
                    listaNotasCredito.reduce((acc, item) => {
                      if (notasCreditoSelectionModel.includes(item.id))
                        return acc + (item.saldoReceber || 0);
                      return acc;
                    }, 0)
                  )}
                </span>
              </h7>
            </Grid>
          </Grid>
          <Grid item xs={12} lg={6}>
            <ChipCompensacao label={"Notas a Compensar"} color={"primary"} />
            <DataGrid
              rows={listaNotasBaixar}
              columns={colunas}
              checkboxSelection
              selectionModel={notasBaixarSelectionModel}
              onRowSelectionModelChange={(selectedIds) => {
                setNotasBaixarSelectionModel(selectedIds);
              }}
              paginationMode={"server"}
              disableAcoes
              noFilter
            />
            <Grid
              container
              xs={12}
              justifyContent={"flex-end"}
              className="mt-2"
            >
              <h7>
                Total Notas a Compensar:{" "}
                <span className="fw-bolder">
                  {formatPrice(
                    listaNotasBaixar.reduce((acc, item) => {
                      if (notasBaixarSelectionModel.includes(item.id))
                        return acc + (item.saldoReceber || 0);
                      return acc;
                    }, 0)
                  )}
                </span>
              </h7>
            </Grid>
          </Grid>
        </Grid>
        <Grid
          container
          spacing={2}
          direction={"row"}
          className="mt-2"
          justifyContent={"flex-end"}
        >
          <Grid item xs={12} lg={3}>
            <Button
              variant="contained"
              className="rounded-pill"
              disabled={
                (!listaNotasCredito.length && !listaNotasBaixar.length) ||
                (!notasCreditoSelectionModel?.length &&
                  !notasBaixarSelectionModel?.length)
              }
              color="primary"
              onClick={() => onSubmit()}
              size="small"
              fullWidth
            >
              Compensar
              <i
                className="ph-bold ph-arrow-right ms-2"
                style={{ fontSize: "clamp(12px, 4vw, 15px)", color: "#fff" }}
              ></i>
            </Button>
          </Grid>
        </Grid>
      </DialogContent>
      <Loading loading={loading} />
    </Dialog>
  );
};

export default CompensacaoDialog;
